/**
 * AnswerOptions — a single-select option list for a Play prompt card's `select`
 * field (INTAKE-PRD §2.8.1). Lifts the aria-pressed option-row pattern from
 * AskUserQuestions (list-rows, keyboard 1–9, focus) as a controlled block the
 * Play driver mounts via `renderInput`, so the intake accept/adjust/reject and
 * classify choices render inside the one Play walk — never a second stepper.
 *
 * Controlled: the host owns `value` (the chosen option id) + `onChange`. When
 * `allowOther`, an inline free-text input activates once the `__new__`-style
 * option is selected (the typed value doubles as the new object type name); the
 * host owns `otherValue`/`onOtherChange`. Composes @excellent/ui atoms only.
 */

import * as React from "react";
import { cn } from "../../utils";
import { Input } from "../../input";
import { Icon } from "../../icon";

export interface AnswerOption {
  /** Stable id returned as the chosen value. */
  id: string;
  /** Bold leading label. */
  title: string;
  /** Secondary muted text after the title. */
  description?: string;
}

export interface AnswerOptionsProps {
  options: AnswerOption[];
  /** The chosen option id (controlled). */
  value: string | null;
  onChange: (id: string) => void;
  /** Show the inline free-text input when the `__new__`-style option is picked. */
  allowOther?: boolean;
  otherValue?: string;
  onOtherChange?: (v: string) => void;
  otherPlaceholder?: string;
  className?: string;
}

/** The option whose selection reveals the allowOther free-text input. */
const OTHER_OPTION_ID = "__new__";

export function AnswerOptions({
  options,
  value,
  onChange,
  allowOther,
  otherValue = "",
  onOtherChange,
  otherPlaceholder = "Name the new object type…",
  className,
}: AnswerOptionsProps) {
  const showOther = allowOther && value === OTHER_OPTION_ID;

  // Keyboard: 1–9 selects the nth option (parity with AskUserQuestions), unless
  // the focus is inside the free-text input.
  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    const n = Number(e.key);
    if (Number.isInteger(n) && n >= 1 && n <= options.length) {
      e.preventDefault();
      onChange(options[n - 1]!.id);
    }
  };

  return (
    <div className={cn("flex flex-col gap-3", className)} onKeyDown={onKeyDown}>
      <div className="flex flex-col">
        {options.map((o, i) => {
          const active = value === o.id;
          return (
            // button-ignore: selectable answer option (list-row, aria-pressed), not an action button
            <button
              type="button"
              key={o.id}
              onClick={() => onChange(o.id)}
              aria-pressed={active}
              autoFocus={i === 0}
              className={cn(
                "group flex items-start gap-3 rounded-md border px-3 py-2.5 text-left transition-colors",
                "border-b border-border/40 first:rounded-t-md last:rounded-b-md",
                active ? "border-primary bg-selected" : "border-transparent hover:bg-selected/50",
              )}
            >
              <span
                className={cn(
                  "mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-primary-foreground transition-colors",
                  active ? "border-primary bg-primary" : "border-border bg-transparent",
                )}
              >
                {active && <Icon name="check" size="xs" className="h-3 w-3" />}
              </span>
              <span className="flex min-w-0 flex-1 flex-row flex-wrap items-baseline gap-1.5">
                <span className="type-body font-medium text-foreground">{o.title}</span>
                {o.description && (
                  <span className="type-body text-muted-foreground">{o.description}</span>
                )}
              </span>
              <span className="ml-auto shrink-0 self-center type-subtext tabular-nums text-muted-foreground opacity-0 group-hover:opacity-100">
                {i + 1}
              </span>
            </button>
          );
        })}
      </div>

      {showOther && (
        <Input
          value={otherValue}
          onChange={(e) => onOtherChange?.(e.target.value)}
          placeholder={otherPlaceholder}
          aria-label="New object type name"
          autoFocus
        />
      )}
    </div>
  );
}
